// src/contexts/ProfileContext.tsx
import { logger } from '../utils/logger';
// ðŸŽ¯ Caches profile data and completion status to prevent re-checking on every tab switch
// âš¡ Loads once and updates only when profile changes
// ðŸ†• FIX: Auto-creates missing profiles on first load

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { profileService } from '../services/profileService';
import { supabase } from '../services/supabase';
import { Profile } from '../types/profile';
import { isProfileComplete } from '../utils/profileUtils';

interface ProfileContextValue {
  profile: Profile | null;
  isComplete: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  updateLocalProfile: (updates: Partial<Profile>) => void;
}

const ProfileContext = createContext<ProfileContextValue | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isComplete, setIsComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  // Load profile data once on mount
  const loadProfile = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      let data = await profileService.getCurrentProfile();

      // ðŸ†• FIX: If profile doesn't exist, create it automatically
      if (!data) {
        logger.log('âš ï¸ Profile not found on initial load, creating automatically...');
        const createResult = await profileService.createProfileIfMissing();

        if (createResult.success) {
          logger.log('âœ… Profile created successfully, reloading...');
          // Reload the newly created profile
          data = await profileService.getCurrentProfile();

          if (data) {
            logger.log('âœ… Profile loaded after creation');
          } else {
            logger.error('âŒ Profile still not found after creation');
          }
        } else {
          logger.error('âŒ Failed to create profile:', createResult.error);
        }

        // If profile still doesn't exist after creation attempt, log out
        if (!data) {
          logger.error('âŒ Unable to create or load profile, logging out...');
          await supabase.auth.signOut();
          window.location.href = '/login';
          return;
        }
      }

      if (!mountedRef.current) return;

      setProfile(data);
      setIsComplete(isProfileComplete(data));
    } catch (error) {
      logger.error('Error loading profile:', error);
      if (mountedRef.current) {
        setProfile(null);
        setIsComplete(false);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    loadProfile();

    return () => {
      mountedRef.current = false;
    };
  }, [loadProfile]);

  // Listen for profile updates from database
  useEffect(() => {
    if (!profile?.id) return;

    const channel = supabase
      .channel('profile-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${profile.id}`,
        },
        () => {
          // Profile was updated in database, refresh our cache
          loadProfile();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [profile?.id, loadProfile]);

  // Listen for custom events from EditProfile, ManageGames, etc.
  useEffect(() => {
    const handleProfileUpdate = () => {
      loadProfile();
    };

    window.addEventListener('profile-updated', handleProfileUpdate);

    return () => {
      window.removeEventListener('profile-updated', handleProfileUpdate);
    };
  }, [loadProfile]);

  // Manual refresh function
  const refresh = useCallback(async () => {
    setLoading(true);
    await loadProfile();
  }, [loadProfile]);

  // Update local profile without database call (optimistic update)
  const updateLocalProfile = useCallback((updates: Partial<Profile>) => {
    setProfile(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      setIsComplete(isProfileComplete(updated));
      return updated;
    });
  }, []);

  const value: ProfileContextValue = {
    profile,
    isComplete,
    loading,
    refresh,
    updateLocalProfile,
  };

  return <ProfileContext.Provider value={value}>{children}</ProfileContext.Provider>;
};

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

// Helper function to emit profile update events
export const emitProfileUpdate = () => {
  window.dispatchEvent(new Event('profile-updated'));
};